import React, {useEffect, useMemo} from 'react';
import {BrowserRouter, Route, Routes, Navigate, Outlet, useNavigate, useLocation} from 'react-router-dom';
import {
  SignupScreen,
  LoginScreen,
  DashboardScreen,
  LandingScreen,
  SpecializationScreen,
  FavoritesScreen,
  SpecialistDisplayScreen,
  ConsultationsScreen,
  ProfileScreen,
  SpecialistPatients,
  SpecialistEarnings,
  SpecialistRatings,
  HelpScreen,
  ActivityScreen,
  SettingsScreen,
  ConsultationSummary,
  SpecialistProfileScreen,
  AdminScreen,
  AccessControlScreen,
  PatientSettingsScreen,
  PatientPayments,
  SpecialistPayouts,
  OTPScreen,
  HospitalProfileScreen,
  TermsScreen,
  PrivacyScreen,
  AuditLogScreen,
} from '@/screens';
import {Routes as WebRoutes} from './Routes';
import {useDispatch, useSelector} from 'react-redux';
import {authStatus, authSuccessStatus, patientDashboardPaymentStatus} from '@/services/redux/selectors';
import {
  resetAuthFormData,
  resetPersistData,
  addConsultationReportRequest,
  resetPatientDashboardState,
  setDashboardSearchTerm,
  setCurrentConsultationTab,
  setSpecialistCurrentConsultationTab,
  resetAuthSession,

  // profile related requests
  getHospitalProfileRequest,
  getProfileRequest,
  getSpecialistProfileRequest,
  resetAuthMessage,
  getHospitalListRequest,
} from '@/services/redux/slices';
import {DashboardScreenLayout, AuthLayout} from '@/components';
import {clearPreSignupFilesToken, clearSignupLicenseFile, matchesRoute} from '@/utils/common_functions';
import {useRouteLeave, useRole} from '@/hooks';
import {ConsultationGroup, SUCCESS_KEY, SPECIALIST_FILTER_SPECIALITY_KEY} from '@/constants';
import {SpecialistAdminOnboard} from '@/screens/specialist-admin-screens/SpecialistAdminOnboard';
import {SpecialistProfileDetailView} from '@/components/specialist-admin';

const ProtectedRoute = () => {
  const isAuthenticated = useSelector(authStatus);
  const fallback = WebRoutes.LOGIN;
  return isAuthenticated ? <Outlet /> : <Navigate to={fallback} />;
};

export function RootNavigator() {
  return (
    <BrowserRouter>
      <RoutesWrapper />
    </BrowserRouter>
  );
}

export function RoutesWrapper() {
  // ================================
  // hooks
  // ================================
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const {pathname} = useLocation();
  const {isPatient, isSpecialist, isHospitalPoc, isSupportAdmin, isFinanceAdmin, isSpecialistAdmin} = useRole();

  // ================================
  // selectors
  // ================================
  const authSuccess = useSelector(authSuccessStatus);
  const paymentStatus = useSelector(patientDashboardPaymentStatus);
  const isAuthenticated = useSelector(authStatus);

  // ================================
  // states
  // ================================
  const isPublicRoute = useMemo(() => {
    return [WebRoutes.LOGIN, WebRoutes.SIGNUP, WebRoutes.VERIFY_OTP, WebRoutes.VERIFY_MFA, WebRoutes.HOME].includes(location.pathname as any);
  }, [location.pathname]);

  // ================================
  // side effects
  // ================================
  // fetch user profile when authenticated and on protected routes
  useEffect(() => {
    // ignore is user is not authenticated
    if (!isAuthenticated) return;
    // ignore if user is not inside protected routes
    if (isPublicRoute) return;

    // fetch hospital profile but skip when user is on profile page
    if (isHospitalPoc && location.pathname !== WebRoutes.PROFILE) dispatch(getHospitalProfileRequest());
  }, [isAuthenticated, isHospitalPoc, isPublicRoute]);

  // fetch hospital list on app load
  useEffect(() => {
    if (isAuthenticated && isSpecialistAdmin) {
      dispatch(getHospitalListRequest());
    }
  }, [isAuthenticated, isSpecialistAdmin]);

  /**
   * check if user successfully logged in successfully by checking there auth success code
   */
  useEffect(() => {
    if (!authSuccess) return;
    if (['S-10001', 'S-10003'].includes(authSuccess as string)) {
      if (authSuccess === 'S-10001') {
        clearPreSignupFilesToken();
        clearSignupLicenseFile();
      }

      // Clear patient dashboard state to ensure no old data persists after signup
      dispatch(resetPatientDashboardState());

      // Redirect support admin to support-tickets route, finance admin to patient payments, others to dashboard
      if (isSupportAdmin) {
        navigate(WebRoutes.SUPPORT_TICKETS);
      } else if (isFinanceAdmin) {
        navigate(WebRoutes.PATIENT_PAYMENTS);
      } else {
        navigate(WebRoutes.DASHBOARD);
      }

      // reset the form
      dispatch(resetAuthFormData());
      dispatch(resetPersistData());
    }
    if (authSuccess === 'S-10005') {
      dispatch(resetAuthMessage());
    }
  }, [authSuccess, isSupportAdmin, navigate, dispatch]);

  useEffect(() => {
    document.getElementById('app-root')?.scrollTo(0, 0);
  }, [pathname]);

  // reset dashboard related states when leaving dashboard page
  useRouteLeave(WebRoutes.DASHBOARD, () => {
    if (paymentStatus === SUCCESS_KEY) {
      dispatch(resetPatientDashboardState());
    }
  });

  // reset consultation tab and search term when leaving consultation page
  useRouteLeave(WebRoutes.CONSULTATION, () => {
    dispatch(setDashboardSearchTerm(''));
    dispatch(setCurrentConsultationTab(ConsultationGroup.Ongoing));
    dispatch(setSpecialistCurrentConsultationTab(ConsultationGroup.Ongoing));
  });

  // reset filter when leaving specialist page
  useRouteLeave(WebRoutes.SPECIALISTS, (destination) => {
    if (matchesRoute(destination, WebRoutes.SPECIALIST_PROFILE_VIEW)) return;
    localStorage.removeItem(SPECIALIST_FILTER_SPECIALITY_KEY);
  })

  return (
    <Routes>
      <Route path={WebRoutes.INDEX} element={<LandingScreen />} />

      <Route path={WebRoutes.TERMS} element={<TermsScreen />} />
      <Route path={WebRoutes.PRIVACY} element={<PrivacyScreen />} />

      {/* all auth routes in the auth layout */}
      <Route element={<AuthLayout />}>
        <Route path={WebRoutes.LOGIN} element={<LoginScreen />} />
        <Route path={WebRoutes.SIGNUP} element={<SignupScreen key="patient-signup" />} />
        <Route path={WebRoutes.SPECIALIST_SIGNUP} element={<SignupScreen key="specialist-signup" />} />
        <Route path={WebRoutes.VERIFY_OTP} element={<OTPScreen key="otp" />} />
        <Route path={WebRoutes.VERIFY_MFA} element={<OTPScreen key="mfa" />} />
      </Route>

      <Route element={<ProtectedRoute />}>
        <Route element={<DashboardScreenLayout />}>
          {/* patient specific routes */}
          <Route path={WebRoutes.SPECIALISTS} element={<SpecializationScreen />} />
          <Route path={WebRoutes.FAVORITES} element={<FavoritesScreen />} />
          <Route path={WebRoutes.SPECIALIST_DISPLAY} element={<SpecialistDisplayScreen />} />
          <Route path={WebRoutes.SPECIALIST_PROFILE_VIEW} element={<SpecialistProfileScreen />} />
          <Route path={WebRoutes.CONSULTATION} element={<ConsultationsScreen />} />
          <Route path={WebRoutes.CONSULTATION_SUMMARY} element={<ConsultationSummary />} />
          <Route path={WebRoutes.HOSPITAL_PROFILE_VIEW} element={<HospitalProfileScreen />} />

          {/* admin specific routes */}
          <Route path={WebRoutes.ADMIN} element={<AdminScreen />} />
          <Route path={WebRoutes.ACTIVITY} element={<ActivityScreen />} />
          <Route path={WebRoutes.RBAC} element={<AccessControlScreen />} />
          <Route path={WebRoutes.AUDIT_LOG} element={<AuditLogScreen />} />

          {/* Specialist specific routes */}
          <Route path={WebRoutes.PATIENTS} element={<SpecialistPatients />} />
          <Route path={WebRoutes.EARNINGS} element={<SpecialistEarnings />} />
          <Route path={WebRoutes.RATINGS} element={<SpecialistRatings />} />

          {/* Patient Admin specific routes */}
          <Route path={WebRoutes.PATIENT_DISPLAY} element={<PatientSettingsScreen />} />

          {/* common routes */}
          <Route path={WebRoutes.SETTINGS} element={<SettingsScreen />} />
          <Route path={WebRoutes.DASHBOARD} element={<DashboardScreen />} />
          <Route path={WebRoutes.SUPPORT_TICKETS} element={<DashboardScreen />} />
          <Route path={WebRoutes.PROFILE} element={<ProfileScreen />} />
          <Route path={WebRoutes.HELP} element={<HelpScreen />} />

          {/* Specialist Admin specific routes */}
          <Route path={WebRoutes.SPECIALIST_ADMIN_ONBOARDING_SPECIALIST} element={<SpecialistAdminOnboard />} />

          {/* Finance Admin specific routes */}
          <Route path={WebRoutes.PATIENT_PAYMENTS} element={<PatientPayments />} />
          <Route path={WebRoutes.SPECIALIST_PAYOUTS} element={<SpecialistPayouts />} />
          <Route path={WebRoutes.SPECIALIST_ADMIN_PROFILE_DETAIL} element={<SpecialistProfileDetailView />} />
        </Route>
      </Route>
    </Routes>
  );
}
